import { useCallback, useEffect, useMemo } from 'react';
import { useCartStore } from '../context/cart/CartStore';
import { useSearch } from '../context/SearchContext';
import type { MenuSectionDto } from '../../generated-api';
import type { AddItemDto } from '../../types/cart';
import { useActiveSections } from '../context/MenuSectionsStore';
import { convertToGrams, findItemByBarcode } from '../utils/barcodeParser';

interface UseBarcodeScanner {
  filteredSections: MenuSectionDto[]; // Filtered sections for fallback search matching
  zoneId: string; // Location timezone for opening hours filtering
}

export const useBarcodeScanner = ({ filteredSections, zoneId }: UseBarcodeScanner) => {
  const { searchText, setSearchText, onSubmitCallbackRef, searchInputRef, setSearchError } = useSearch();
  const addMenuItem = useCartStore((x) => x.addMenuItem);
  const activeSections = useActiveSections(zoneId);

  // Get all items from active sections for barcode matching (respects opening hours)
  const allItems = useMemo(() => activeSections.flatMap((section) => section.items), [activeSections]);

  // Helper function to clear search field and blur input
  const clearSearchAndBlur = useCallback(() => {
    setSearchText('');
    // Blur the search input field
    if (searchInputRef?.current) {
      searchInputRef.current.blur();
    } else {
      // Fallback: try to find and blur the search input
      const searchInput = document.querySelector('input[aria-label="Search Bar"]') as HTMLInputElement;
      if (searchInput) {
        searchInput.blur();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Automatically add item to cart when Enter is pressed and only 1 item is filtered
  const addSingleFilteredItemToCart = useCallback(async () => {
    // Check if search text is empty
    if (!searchText) {
      return;
    }

    // Store search text before processing (we'll clear it after, but keep error messages)
    const currentSearchText = searchText;

    // Try to find item by barcode parsing first (use all items, not just filtered)
    const barcodeMatch = findItemByBarcode(currentSearchText, allItems);

    if (barcodeMatch) {
      const { item, quantity, calculatedPrice, isWeightItem } = barcodeMatch;

      // Check if item has options
      if (item.optionGroups?.length > 0) {
        setSearchError('Product has options and must be added manually');
        clearSearchAndBlur(); // Clear search field and blur after scan, but keep error message
        return;
      }

      // Check if item is a gift card
      if (item.giftCardId) {
        setSearchError('Gift card must be added manually');
        clearSearchAndBlur(); // Clear search field and blur after scan, but keep error message
        return;
      }

      // Handle weight items
      if (isWeightItem) {
        // If quantity is 0, it means we need manual weight entry
        // For now, we'll skip items without embedded weight in barcode scanning
        if (quantity === 0) {
          setSearchError('Please enter weight manually or scan a weight barcode');
          clearSearchAndBlur(); // Clear search field and blur after scan, but keep error message
          return;
        }

        // Backend requires unitQuantity in grams as an integer >= 1
        // Convert weightKg to grams (backend always expects grams, regardless of priceType)
        const unitQuantity =
          barcodeMatch.weightKg === undefined
            ? Math.max(1, Math.ceil(quantity)) // Fallback if weightKg not available
            : convertToGrams(barcodeMatch.weightKg);

        // Weight barcode with embedded weight - add directly
        const addItem: AddItemDto = {
          courseId: item.courseId,
          giftCardId: item.giftCardId,
          itemVariantId: item.variantLinkId,
          menuSectionId: item.menuSectionId,
          name: item.name,
          openPrice: true,
          options: [],
          price: calculatedPrice, // Use actual calculated price based on real weight
          sectionProductId: item.sectionProductId,
          takeawayPrice: item.takeawayPrice ? item.takeawayPrice * quantity : calculatedPrice,
          unitQuantity, // Send in grams for backend
        };

        try {
          await addMenuItem(addItem);
          clearSearchAndBlur(); // Clear search field and blur after adding item
        } catch (error) {
          setSearchError(error instanceof Error ? error.message : 'Could not add item to cart');
          clearSearchAndBlur(); // Clear search field and blur even on error
        }
        return;
      }

      // Fixed price item
      const addItem: AddItemDto = {
        courseId: item.courseId,
        giftCardId: item.giftCardId,
        itemVariantId: item.variantLinkId,
        menuSectionId: item.menuSectionId,
        name: item.name,
        openPrice: false,
        options: [],
        price: calculatedPrice || item.price,
        sectionProductId: item.sectionProductId,
        takeawayPrice: item.takeawayPrice,
      };

      try {
        await addMenuItem(addItem);
        clearSearchAndBlur(); // Clear search field and blur after adding item
      } catch (error) {
        setSearchError(error instanceof Error ? error.message : 'Could not add item to cart');
        clearSearchAndBlur(); // Clear search field and blur even on error
      }
      return;
    }

    // Check if search text looks like a barcode (13 digits)
    // If it does and barcode parsing didn't find a match, show error instead of falling back to search
    const isBarcodeFormat = /^\d{13}$/.test(currentSearchText.trim());
    if (isBarcodeFormat) {
      setSearchError('No products found for this barcode');
      clearSearchAndBlur(); // Clear search field and blur after scan, but keep error message
      return;
    }

    // Fallback to old search-based matching if barcode parsing didn't find a match
    // Count total number of filtered items
    const totalFilteredItems = filteredSections.reduce((sum, section) => sum + section.items.length, 0);

    // Check if no items found
    if (totalFilteredItems === 0) {
      setSearchError('No products found');
      clearSearchAndBlur(); // Clear search field and blur after scan, but keep error message
      return;
    }

    // Check if multiple items found
    if (totalFilteredItems > 1) {
      setSearchError(`${totalFilteredItems} products found. Please select one.`);
      clearSearchAndBlur(); // Clear search field and blur after scan, but keep error message
      return;
    }

    // Find the only filtered item
    const singleItem = filteredSections.flatMap((section) => section.items)[0];
    if (!singleItem) {
      setSearchError('Product not found');
      clearSearchAndBlur(); // Clear search field and blur after scan, but keep error message
      return;
    }

    // Check if item has options
    if (singleItem.optionGroups?.length > 0) {
      setSearchError('Product has options and must be added manually');
      clearSearchAndBlur(); // Clear search field and blur after scan, but keep error message
      return;
    }

    // Check if item has open price
    if (singleItem.priceType === 'OPEN_PRICE') {
      setSearchError('Product has open price and must be added manually');
      clearSearchAndBlur(); // Clear search field and blur after scan, but keep error message
      return;
    }

    // Handle weight items in fallback search
    const isWeightItem = ['PER_GR', 'PER_GRAM', 'PER_HG', 'PER_KG'].includes(singleItem.priceType || '');
    if (isWeightItem) {
      // Weight items without embedded weight need manual entry
      setSearchError('Please enter weight manually or scan a weight barcode');
      clearSearchAndBlur(); // Clear search field and blur after scan, but keep error message
      return;
    }

    // Check if item is a gift card
    if (singleItem.giftCardId) {
      setSearchError('Gift card must be added manually');
      clearSearchAndBlur(); // Clear search field and blur after scan, but keep error message
      return;
    }

    const addItem: AddItemDto = {
      courseId: singleItem.courseId,
      giftCardId: singleItem.giftCardId,
      itemVariantId: singleItem.variantLinkId,
      menuSectionId: singleItem.menuSectionId,
      name: singleItem.name,
      openPrice: false,
      options: [],
      price: singleItem.price,
      sectionProductId: singleItem.sectionProductId,
      takeawayPrice: singleItem.takeawayPrice,
    };

    try {
      await addMenuItem(addItem);
      clearSearchAndBlur(); // Clear search field and blur after adding item
    } catch (error) {
      // Set error message to be displayed below search bar
      setSearchError(error instanceof Error ? error.message : 'Could not add item to cart');
      clearSearchAndBlur(); // Clear search field and blur even on error
    }
  }, [allItems, filteredSections, searchText, addMenuItem, setSearchError, clearSearchAndBlur]);

  // Set callback for when Enter is pressed in SearchBar
  useEffect(() => {
    onSubmitCallbackRef.current = addSingleFilteredItemToCart;
  }, [addSingleFilteredItemToCart, onSubmitCallbackRef]);

  // Focus on SearchBar when a number or letter is typed, blur on ESC
  useEffect(() => {
    const getSearchInput = () => document.querySelector('input[aria-label="Search Bar"]') as HTMLInputElement | null;

    const handleKeyDown = (event: KeyboardEvent) => {
      const searchInput = getSearchInput();
      if (!searchInput) {
        return;
      }

      if (event.key === 'Escape') {
        searchInput.blur();
        return;
      }

      // Don't steal focus if we are already in an input field
      const { activeElement } = document;
      if (activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA') {
        return;
      }

      if (/^[0-9a-zA-Z]$/.test(event.key)) {
        searchInput.focus();
        if (searchInputRef) {
          searchInputRef.current = searchInput;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchInputRef]);
};
